import type { AgentTool, AgentToolDetail, BuiltinToolKind, VendorTool } from '@onefootprint/request-types/dashboard';

// The tools catalog returns `kind` as a ProviderIntegrationKind (snake_case at runtime), while the
// graph stores a VendorTool keyed by `vendorKind`. These line up 1:1 except for TransUnion, whose
// stored variant uses a shortened name.
const PROVIDER_TO_VENDOR_KIND: Record<string, string> = {
  trans_union_tru_vision_resident_screening: 'truvision_resident_screening',
};

export const toVendorKind = (providerKind: string): string => PROVIDER_TO_VENDOR_KIND[providerKind] ?? providerKind;

// Percy's built-in tools share the catalog with vendors; their graph entry is the bare kind.
const BUILTIN_TOOL_KINDS: ReadonlySet<string> = new Set<BuiltinToolKind>(['entity_duplicates', 'osint', 'browser']);

// What a new agent starts with (mirrors `AgentTool::default_tools`).
export const DEFAULT_BUILTIN_KINDS: BuiltinToolKind[] = ['osint', 'browser'];

export const withDefaultBuiltinTools = (catalogKinds: string[]): string[] => [
  ...DEFAULT_BUILTIN_KINDS.filter(kind => !catalogKinds.includes(kind)),
  ...catalogKinds,
];

// Built-in tools are always available; a vendor needs a credential the catalog reports on.
export const isToolAvailable = (tool: AgentToolDetail): boolean => tool.toolKind !== 'vendor' || !tool.disabledReason;

// The key a catalog entry is enabled under on the graph.
export const toolKeyForCatalogKind = (catalogKind: string): string =>
  BUILTIN_TOOL_KINDS.has(catalogKind) ? catalogKind : toVendorKind(catalogKind);

export const graphToolForCatalogKind = (catalogKind: string): AgentTool =>
  BUILTIN_TOOL_KINDS.has(catalogKind)
    ? { kind: 'builtin', data: { kind: catalogKind as BuiltinToolKind } }
    : { kind: 'vendor', data: { vendorKind: toVendorKind(catalogKind) } as VendorTool };

// The catalog key of a vendor or built-in tool; MCP tools are keyed by server id instead.
export const toolKey = (tool: AgentTool): string | undefined => {
  if (tool.kind === 'vendor') return tool.data.vendorKind;
  if (tool.kind === 'builtin') return tool.data.kind;
  return undefined;
};

export const enabledToolKeys = (tools: AgentTool[]): Set<string> => new Set(tools.flatMap(tool => toolKey(tool) ?? []));

// Fallback display name for when the tools catalog hasn't loaded (or a vendor was removed from it).
export const humanizeVendorKind = (kind: string): string =>
  kind.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
